import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router';
import classnames from 'classnames';
import debounce from 'debounce';
import queryString from 'query-string';
import InputField from '../inputField/InputField';
import Button from '../button/Button';
import CloseButton from '../closeButton/CloseButton';
import {
    ROUTES,
    SEARCH_RESOURCES,
} from '../../js/_constants';
import SearchResourceSelector from '../searchResourceSelector/SearchResourceSelector';
import { useTranslation } from 'react-i18next';
import { searchStore, useSuggestionsStore , generalStore} from '../../store';

import './SearchInput.scss';

const SearchInput = () => {
    const { t } = useTranslation();

    const { isLoadingSuggestions, autocompleteSuggestions ,setSearchSuggestions, getSearchSuggestions } = useSuggestionsStore() as { isLoadingSuggestions: boolean; autocompleteSuggestions: any[]; setSearchSuggestions: (suggestions: any[]) => void; getSearchSuggestions: (value: string) => void };
    const { setSearchResource, setIsFromSearchInput, searchResource } = searchStore();
    const { setIsSearchBarOpen, isSearchBarOpen } = generalStore();

    const navigate = useNavigate();
    const inputRef = useRef<any>(null);

    const [value, setValue] = useState('');
    const [currentSearchResource, setCurrentSearchResource] =
        useState<any>(searchResource);

    const debouncedGetSearchSuggestions = useRef(
        debounce(value => {
            getSearchSuggestions(value);
        }, 500)
    );

    useEffect(() => {
        setCurrentSearchResource(searchResource);
    }, [searchResource]);

    useEffect(() => {
        if (isSearchBarOpen) {
            inputRef.current?.focus();
        }
    }, [isSearchBarOpen]);

    const doSearch = (value) => {
        setSearchResource(currentSearchResource);

        localStorage.setItem('search-resource', currentSearchResource);

        const defaultQueryParams = {page: 1};

        switch (currentSearchResource) {
            case SEARCH_RESOURCES.DEALERSHIP:
                navigate({
                    pathname: ROUTES.DEALERSHIPS,
                    search: queryString.stringify({ query: value })
                });
                break;
            case SEARCH_RESOURCES.DEVICE:
                navigate({
                    pathname: ROUTES.DEVICES,
                    search: queryString.stringify({query: value})
                });
                break;
            case SEARCH_RESOURCES.MEDIA:
                localStorage.setItem('last-search', value);
                {
                    let query, tag, title;
                    if(value.includes('Tag: ')){
                        tag = value.split('Tag: ')[1];
                    } else if(value.includes('Title: ')){
                        title = value.split('Title: ')[1];
                    } else {
                        query = value;
                    }
                    setIsFromSearchInput(true);
                    setSearchSuggestions([]);
                    navigate({
                        pathname: ROUTES.MEDIA_LIBRARY,
                        search: queryString.stringify({
                            ...defaultQueryParams,
                            ...(tag && { tag }),
                            ...(title && { title }),
                            ...(query && { query })
                        })
                    });
                }
                break;
            case SEARCH_RESOURCES.PLAYLIST:
                navigate({
                    pathname: ROUTES.PLAYLISTS,
                    search: queryString.stringify({
                        ...defaultQueryParams,
                        title: value
                    })
                });
                break;

            default:
                break;
        }
    };

    const onSubmit = (newValue: string | number = '') => {
        if (newValue) {
            setValue('');
            doSearch(String(newValue));
        }
    };

    const onValueChange = newValue => {
        if (currentSearchResource === SEARCH_RESOURCES.MEDIA) {
            if (newValue.length > 1) {
                debouncedGetSearchSuggestions.current(newValue);
            } else {
                debouncedGetSearchSuggestions.current.clear();
                setSearchSuggestions([]);
            }
        }
        setValue(newValue); 
    };

    const closeSearch = () => {
        setIsSearchBarOpen(false);
    };

    const onOptionClickHandler = newSearchResource => {
        setCurrentSearchResource(newSearchResource);
    };

    const generateLabel = (resource) => {
        switch (resource) {
            case 'media':
                return t('searchInput');
            case 'device':
                return t('searchInput_devices');
            case 'playlist':
                return t('searchInput_playlists');
            case 'dealership':
                return t('searchInput_dealerships');
            default:
                break;
        }
    };

    const label = generateLabel(currentSearchResource) || '';

    const classes = classnames({
        'b-search-input': true,
        'is-hidden': !isSearchBarOpen
    });

    return (
        <div className={classes}>
            <div className="search-container">
                <div className="field-container">
                    <InputField
                        clearIcon
                        hideLabel={false}
                        inputType={
                            currentSearchResource === SEARCH_RESOURCES.MEDIA
                                ? 'autocomplete'
                                : 'input'
                        }
                        name={label}
                        label={label}
                        value={value}
                        isLoadingSuggestions={isLoadingSuggestions}
                        suggestions={autocompleteSuggestions}
                        onSubmitHandler={onSubmit}
                        onValueChangeHandler={newValue =>
                            onValueChange(newValue)
                        }
                        reference={inputRef}
                    />
                    <Button
                        extraClass="search-button"
                        clickHandler={() => onSubmit(value)}
                        disabled={!value}
                        name="search-button"
                    >
                        <span className="search-icon" />
                    </Button>
                </div>
                <SearchResourceSelector
                    onOptionClick={onOptionClickHandler}
                    searchResource={currentSearchResource}
                />
            </div>
            <CloseButton onClick={closeSearch} />
        </div>
    );
};

export default SearchInput;
